// Mirrors platform.payment_method_configs / audit_log / subscription_plans /
// subscription_payments / billing_cycles in CliqSuite_DB_Architecture.dc.html.
// platform.admin_users is intentionally NOT exposed here — see core.ts's
// note on staffSchema for why (password_hash must never sync to clients).
import { z } from "zod";

export const paymentMethodConfigSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(["qr", "bank", "wallet"]),
  name: z.string(),
  image_data: z.string().nullable(),
  instructions: z.string().nullable(),
  bank_name: z.string().nullable(),
  account_name: z.string().nullable(),
  account_number: z.string().nullable(),
  branch: z.string().nullable(),
  network: z.string().nullable(),
  wallet_address: z.string().nullable(),
  is_enabled: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type PaymentMethodConfig = z.infer<typeof paymentMethodConfigSchema>;

export const auditLogSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  actor_type: z.enum(["admin", "owner", "system"]),
  actor_id: z.string().uuid().nullable(),
  actor_name: z.string().nullable(),
  actor_role: z.string().nullable(),
  payment_ref: z.string().nullable(),
  detail: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
});
export type AuditLog = z.infer<typeof auditLogSchema>;

export const subscriptionPlanSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  base_price: z.number().int().positive(),
  max_stores: z.number().int().default(1),
  max_staff: z.number().int().default(5),
  max_products: z.number().int().default(100),
  features: z.record(z.string(), z.unknown()).default({}),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>;

export const billingCycleSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  months: z.number().int().positive(),
  multiplier: z.string().default("1.0"),
  created_at: z.string(),
});
export type BillingCycle = z.infer<typeof billingCycleSchema>;

/** A plan's own prices per billing cycle, keyed by the cycle's key (features.cycle_prices). Empty when it has none. */
export function planOwnCyclePrices(plan: Pick<SubscriptionPlan, "features">): Record<string, number> {
  const raw = (plan.features ?? {}).cycle_prices;
  const out: Record<string, number> = {};
  if (raw && typeof raw === "object") for (const [key, value] of Object.entries(raw)) if (Number(value) > 0) out[key] = Math.round(Number(value));
  return out;
}

/**
 * What a plan costs for one billing cycle: its own price for that cycle when the admin set one,
 * otherwise the monthly price times the cycle's multiplier. The multiplier already counts the
 * months paid for (Annual at 10 is twelve months for the price of ten), so months is not a factor.
 */
export function planCyclePrice(plan: Pick<SubscriptionPlan, "base_price" | "features">, cycle: Pick<BillingCycle, "key" | "multiplier">): number {
  return planOwnCyclePrices(plan)[cycle.key] ?? Math.round(plan.base_price * (Number(cycle.multiplier) || 1));
}

// Onboarding presets: business-type -> recommended module set. Reference
// data managed by the platform team, mirroring mm_PRESETS in
// CliqSuite_OwnerPortal.dc.html. Owner Portal reads these to power the
// Modules page's preset selector; it never writes to this table.
export const modulePresetSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  icon: z.string(),
  platform: z.enum(["pos", "service"]).default("pos"),
  modules: z.array(z.string()),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
});
export type ModulePreset = z.infer<typeof modulePresetSchema>;

export const subscriptionPaymentSchema = z.object({
  id: z.string().uuid(),
  reference: z.string(),
  business_id: z.string().uuid(),
  submitted_by_user_id: z.string().uuid().nullable(),
  plan_id: z.string().uuid(),
  billing_cycle_id: z.string().uuid(),
  amount: z.number().int().positive(),
  payment_method_type: z.enum(["qr", "bank", "wallet"]),
  payment_method_name: z.string(),
  proof_data: z.string().nullable(),
  txid: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(["pending", "approved", "rejected"]).default("pending"),
  reviewed_by_admin_id: z.string().uuid().nullable(),
  reviewed_at: z.string().nullable(),
  reject_reason: z.string().nullable(),
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  parent_payment_id: z.string().uuid().nullable(),
  resubmission_count: z.number().int().min(0).max(3).default(0),
  // Total amount knocked off amount above by a standing subscription_discounts
  // match and/or a redeemed discount_codes entry, computed client-side at
  // submission time. discount_code_id only counts toward that code's
  // redeemed_count once this payment is approved (see the /:id/approve
  // endpoint) — a rejected payment doesn't consume a redemption.
  discount: z.number().int().min(0).default(0),
  discount_code_id: z.string().uuid().nullable().default(null),
  submitted_at: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SubscriptionPayment = z.infer<typeof subscriptionPaymentSchema>;

// Standing subscription discount — global (business_id null) or scoped to
// one business, optionally narrowed to one plan (plan_id null = all
// plans). Applied automatically by owner-portal's billing calculation.
export const subscriptionDiscountSchema = z.object({
  id: z.string().uuid(),
  scope: z.enum(["global", "business"]),
  business_id: z.string().uuid().nullable(),
  plan_id: z.string().uuid().nullable(),
  discount_type: z.enum(["percent", "flat"]),
  value: z.number().int().positive(),
  label: z.string().nullable(),
  is_active: z.boolean().default(true),
  expires_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SubscriptionDiscount = z.infer<typeof subscriptionDiscountSchema>;

// Redeemable discount code — entered by an owner at subscription-payment
// time. redeemed_count only increments on payment approval, not submission.
export const discountCodeSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  plan_id: z.string().uuid().nullable(),
  discount_type: z.enum(["percent", "flat"]),
  value: z.number().int().positive(),
  max_redemptions: z.number().int().positive().nullable(),
  redeemed_count: z.number().int().min(0).default(0),
  label: z.string().nullable(),
  is_active: z.boolean().default(true),
  expires_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DiscountCode = z.infer<typeof discountCodeSchema>;
